// PermissionFlow — sequential permission onboarding after registration
// Shows after the welcome overlay: Location → Notifications → Motion (iOS only) → Install
// Props: step (0-2), steps (array), onAllow, onSkip, darkMode

export default function PermissionFlow({ step, steps, onAllow, onSkip, darkMode, confirmMsg }) {
  if (!steps || step >= steps.length) return null;
  const current = steps[step];
  const total = steps.length;
  const isIOSInstall = current.description === "_ios_install_";

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:1050,
      background:"rgba(7,9,12,0.92)",
      backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"24px",
      animation:"fadeInOverlay 0.4s ease",
    }}>
      <div style={{
        width:"100%", maxWidth:"360px",
        background:"var(--s1)",
        border:"1px solid var(--border)",
        borderRadius:"24px",
        padding:"36px 28px 28px",
        textAlign:"center",
        position:"relative",
      }}>

        {/* Step dots */}
        <div style={{display:"flex", gap:"6px", justifyContent:"center", marginBottom:"32px"}}>
          {steps.map((_, i) => (
            <div key={i} style={{
              width: i === step ? "20px" : "6px",
              height:"6px",
              borderRadius:"3px",
              background: i === step ? "var(--accent)" : i < step ? "rgba(0,201,167,0.4)" : "rgba(255,255,255,0.1)",
              transition:"all 0.3s ease",
            }}/>
          ))}
        </div>

        {/* Icon */}
        <div style={{
          width:"72px", height:"72px",
          borderRadius:"20px",
          background: darkMode
            ? `linear-gradient(135deg, ${current.iconBg1}, ${current.iconBg2})`
            : `linear-gradient(135deg, ${current.iconBgL1}, ${current.iconBgL2})`,
          border:`1px solid ${current.iconBorder}`,
          display:"flex", alignItems:"center", justifyContent:"center",
          margin:"0 auto 20px",
          fontSize:"32px",
        }}>
          {current.icon}
        </div>

        {/* Text */}
        <div style={{fontSize:"20px", fontWeight:"800", color:"var(--text)", marginBottom:"10px", letterSpacing:"0.2px"}}>
          {current.title}
        </div>

        {/* iOS install instructions — visual step-by-step */}
        {isIOSInstall ? (
          <div style={{textAlign:"left", margin:"0 auto 32px", maxWidth:"290px"}}>
            <div style={{fontSize:"13px", color:"var(--muted)", lineHeight:1.6, marginBottom:"16px", textAlign:"center"}}>
              Install Sirat as an app on your phone for notifications and the full experience:
            </div>
            {[
              { num:"1", text:<>Tap the <strong style={{color:"var(--text)"}}>Share</strong> button <span style={{fontSize:"18px",verticalAlign:"middle"}}>⎙</span> at the bottom of Safari</> },
              { num:"2", text:<>Scroll down and tap <strong style={{color:"var(--text)"}}>"Add to Home Screen"</strong></> },
              { num:"3", text:<>Tap <strong style={{color:"var(--text)"}}>"Add"</strong> in the top right</> },
            ].map((s, i) => (
              <div key={i} style={{display:"flex", alignItems:"flex-start", gap:"12px", marginBottom:"12px"}}>
                <div style={{
                  width:"26px", height:"26px", borderRadius:"50%", flexShrink:0,
                  background: darkMode ? "rgba(160,100,255,0.15)" : "rgba(160,100,255,0.1)",
                  border:"1px solid rgba(160,100,255,0.3)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:"13px", fontWeight:"800", color:"#a06aff",
                }}>{s.num}</div>
                <div style={{fontSize:"13px", color:"var(--muted)", lineHeight:1.5, paddingTop:"3px"}}>{s.text}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{fontSize:"14px", color:"var(--muted)", lineHeight:1.6, marginBottom:"32px", maxWidth:"280px", margin:"0 auto 32px"}}>
            {current.description}
          </div>
        )}

        {/* Confirmation message */}
        {confirmMsg && (
          <div style={{
            fontSize:"13px", color:"var(--accent)", fontWeight:"700",
            marginBottom:"16px", padding:"10px 16px",
            background:"rgba(0,201,167,0.08)", borderRadius:"12px",
            border:"1px solid rgba(0,201,167,0.2)",
            animation:"fadeInOverlay 0.3s ease",
          }}>
            {confirmMsg}
          </div>
        )}

        {/* Buttons */}
        <button
          onClick={onAllow}
          style={{
            width:"100%", padding:"15px",
            background:`linear-gradient(135deg, ${current.btnGrad1}, ${current.btnGrad2})`,
            border:"none", borderRadius:"14px",
            color: current.btnTextColor || "#07090c",
            fontSize:"16px", fontWeight:"800",
            cursor:"pointer", fontFamily:"inherit",
            marginBottom:"10px", letterSpacing:"0.3px",
            boxShadow:`0 4px 20px ${current.btnShadow}`,
          }}
        >
          {current.allowLabel}
        </button>
        <button
          onClick={onSkip}
          style={{
            width:"100%", padding:"13px",
            background:"transparent",
            border:"1px solid var(--border)",
            borderRadius:"14px",
            color:"var(--muted)",
            fontSize:"14px", fontWeight:"600",
            cursor:"pointer", fontFamily:"inherit",
          }}
        >
          {current.skipLabel || "Skip for now"}
        </button>

        {/* Step counter */}
        <div style={{marginTop:"20px", fontSize:"11px", color:"rgba(148,164,176,0.4)", letterSpacing:"1px"}}>
          {step + 1} of {total}
        </div>
      </div>
    </div>
  );
}
